import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { db, auth, storage } from '../firebaseConfig';
import {
    doc,
    getDoc,
    setDoc,
    Timestamp,
    collection,
    getDocs,
    deleteDoc
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import Toast from './Toast';
import ConfirmationModal from './modals/ConfirmationModalActions';

import { setPipelineRefresh } from '../redux/pipelineRefresh';
import { useAppSelector, useAppDispatch } from '../redux/store';


interface CustomClaims {
    leadUid: string;
    subAgentId: string;
    companyId: string;
    role: string;
}

interface TOEFLData {
    dateOfExam: Date | null;
    speakingScore: string;
    readingScore: string;
    writingScore: string;
    listeningScore: string;
    overallScore: string;
    testType: string;
    TRFNumber: string;
    uploadedResult?: string;
}

interface IELTSData {
    dateOfExam: Date | null;
    speakingScore: string;
    readingScore: string;
    writingScore: string;
    listeningScore: string;
}

interface TestResultsProps {
    write: boolean;
}

interface AdditionalExam {
    id: string;
    examName: string;
    dateOfExam: Date | null;
    score: string;
}

const TestResults = ({ write }: TestResultsProps) => {
    const { leadId: urlLeadId } = useParams();
    const [leadId, setLeadId] = useState(urlLeadId || "");
    const [leadName, setLeadName] = useState("");
    const [leadPhone, setLeadPhone] = useState("");
    const [companyId, setCompanyId] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toeflData, setToeflData] = useState<TOEFLData>({
        dateOfExam: null,
        speakingScore: '',
        readingScore: '',
        writingScore: '',
        listeningScore: '',
        overallScore: '',
        testType: '',
        TRFNumber: '',
    });

    const [ieltsData, setIeltsData] = useState<IELTSData>({
        dateOfExam: null,
        speakingScore: '',
        readingScore: '',
        writingScore: '',
        listeningScore: '',
    });

    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string>('');
    const [isUploading, setIsUploading] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const pipelineRefresh = useAppSelector(state => state.pipelineRefresh.refresh);
    const dispatch = useAppDispatch();
    // modal states
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isAdditionalActions, setAdditionalActions] = useState(false);

    // New states for additional exams
    const [showAddExamForm, setShowAddExamForm] = useState(false); // Add this state
    const [showAddExamPrompt, setShowAddExamPrompt] = useState(false);
    const [newExam, setNewExam] = useState<AdditionalExam>({
        id: '',
        examName: '',
        dateOfExam: null,
        score: ''
    });
    const [additionalExams, setAdditionalExams] = useState<AdditionalExam[]>([]);

    const [collapsibleSections, setCollapsibleSections] = useState({
        toefl: false,
        ielts: false,
    });

    // colaps state for each additional exam added dynamically
    const [collapsibleAdditionalExams, setCollapsibleAdditionalExams] = useState<{ [key: string]: boolean }>({});


    useEffect(() => {
        const fetchAdditionalExams = async () => {
            if (!companyId || !leadId) return;

            try {
                const examsCollection = collection(db, `companies/${companyId}/leads/${leadId}/academics`);
                const snapshot = await getDocs(examsCollection);

                const exams: AdditionalExam[] = [];
                snapshot.forEach(doc => {
                    // Skip TOEFL and IELTS as they're handled separately
                    if (doc.id !== 'TOEFL' && doc.id !== 'IELTS') {
                        const data = doc.data();
                        exams.push({
                            id: doc.id,
                            examName: doc.id,
                            dateOfExam: data.dateOfExam?.toDate() || null,
                            score: data.score || ''
                        });
                    }
                });

                setAdditionalExams(exams);
                // Initialize collapsible state for each additional exam
                const initialCollapsibleState: { [key: string]: boolean } = {};
                exams.forEach(exam => {
                    initialCollapsibleState[exam.id] = false;
                });
                setCollapsibleAdditionalExams(initialCollapsibleState);
            } catch (error) {
                console.error("Error fetching additional exams:", error);
            }
        };

        fetchAdditionalExams();
    }, [companyId, leadId]);

    useEffect(() => {
        if (leadId) {
            const fetchLeadData = async () => {
                try {
                    const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
                    if (leadDoc.exists()) {
                        const data = leadDoc.data();
                        setLeadName(data.name);
                        setLeadPhone(data.phone);
                    }
                } catch (error) {
                    console.error("Error fetching lead data:", error);
                }
            };
            fetchLeadData();
        }
    }, [leadId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const currentUser = auth.currentUser;
                if (!currentUser) return;

                // Get token claims
                const idTokenResult = await currentUser.getIdTokenResult();
                let companyId;

                // Check if we have student claims
                const claims = idTokenResult.claims as unknown as CustomClaims;
                if (claims.leadUid && claims.companyId) {
                    // For student, use claims
                    setLeadId(claims.leadUid);
                    companyId = claims.companyId;
                } else if (claims.subAgentId && claims.companyId) {
                    // For admin, use claims
                    companyId = claims.companyId;
                } else {
                    // For regular users, use URL param and user document
                    if (!urlLeadId) return; // regular users must have leadId in URL
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    companyId = userDoc.data()?.companyId;
                }

                if (!companyId || !leadId) return;
                setCompanyId(companyId);

                // Fetch TOEFL data
                const toeflDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}/academics/TOEFL`));
                if (toeflDoc.exists()) {
                    const data = toeflDoc.data();
                    setToeflData({
                        dateOfExam: data.dateOfExam?.toDate() || null,
                        speakingScore: data.speakingScore || '',
                        readingScore: data.readingScore || '',
                        writingScore: data.writingScore || '',
                        listeningScore: data.listeningScore || '',
                        overallScore: data.overallScore || '',
                        testType: data.testType || '',
                        TRFNumber: data.TRFNumber || '',
                        uploadedResult: data.uploadedResult || '',
                    });
                    if (data.uploadedResult) {
                        setUploadedFileName(data.uploadedResult);
                    }
                }

                // Fetch IELTS data
                const ieltsDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}/academics/IELTS`));
                if (ieltsDoc.exists()) {
                    const data = ieltsDoc.data();
                    setIeltsData({
                        dateOfExam: data.dateOfExam?.toDate() || null,
                        speakingScore: data.speakingScore || '',
                        readingScore: data.readingScore || '',
                        writingScore: data.writingScore || '',
                        listeningScore: data.listeningScore || '',
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [leadId]);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !companyId || !leadId) return;

        try {
            setIsUploading(true);
            const fileName = `${uuidv4()}_${file.name}`;
            const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${fileName}`);

            await uploadBytes(storageRef, file);

            // Update only the uploadedResult field in Firestore
            const toeflRef = doc(db, `companies/${companyId}/leads/${leadId}/academics/TOEFL`);
            const toeflDoc = await getDoc(toeflRef);

            if (toeflDoc.exists()) {
                // If document exists, update only the uploadedResult field
                await setDoc(toeflRef, { uploadedResult: fileName }, { merge: true });
            } else {
                // If document doesn't exist, create it with only the uploadedResult field
                await setDoc(toeflRef, { uploadedResult: fileName });
            }

            setUploadedFileName(fileName);
            setUploadedFile(file);
            setToastMessage('File uploaded successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error("Error uploading file:", error);
            setToastMessage('Error uploading file.Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            // alert("Error uploading file. Please try again.");
        } finally {
            setIsUploading(false);
            if (event.target) {
                event.target.value = ''; // Reset file input after upload
            }
        }
    };

    const handleFileDelete = async () => {
        if (!companyId || !leadId || !uploadedFileName) return;

        try {
            setIsUploading(true);
            const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${uploadedFileName}`);
            await deleteObject(storageRef);

            // Update Firestore to remove the file reference
            await setDoc(doc(db, `companies/${companyId}/leads/${leadId}/academics/TOEFL`), {
                ...toeflData,
                dateOfExam: toeflData.dateOfExam ? Timestamp.fromDate(toeflData.dateOfExam) : null,
                uploadedResult: ''
            });

            setUploadedFileName('');
            setUploadedFile(null);

            // Reset the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            setToastMessage('Deleted file successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error("Error deleting file:", error);
            setToastMessage('Error deleting file. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            // alert("Error deleting file. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileDownload = async () => {
        if (!companyId || !leadId || !uploadedFileName) return;

        try {
            const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${uploadedFileName}`);
            const url = await getDownloadURL(storageRef);
            window.open(url, '_blank');
            setToastMessage('File downloaded successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error("Error downloading file:", error);
            setToastMessage('Error downloading file. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            // alert("Error downloading file. Please try again.");
        }
    };

    const confirmSave = () => {
        // Open confirmation modal
        setIsConfirmModalOpen(true);
    }

    const handleSave = async () => {
        try {
            setIsLoading(true);
            if (!companyId || !leadId) return;

            // Helper function to sanitize data (convert empty strings to null)
            const sanitizeData = (data: any) => {
                const sanitized: Record<string, any> = {};
                Object.keys(data).forEach(key => {
                    if (data[key] === undefined) {
                        // Skip undefined values
                        return;
                    }
                    sanitized[key] = (typeof data[key] === 'string' && data[key].trim() === '')
                        ? null
                        : data[key];
                });
                return sanitized;
            };

            // Sanitize and save TOEFL data
            const sanitizedToefl = sanitizeData({
                ...toeflData,
                dateOfExam: toeflData.dateOfExam ? Timestamp.fromDate(toeflData.dateOfExam) : null,
                uploadedResult: uploadedFileName
            });
            await setDoc(
                doc(db, `companies/${companyId}/leads/${leadId}/academics/TOEFL`),
                sanitizedToefl
            );

            // Sanitize and save IELTS data
            const sanitizedIelts = sanitizeData({
                ...ieltsData,
                dateOfExam: ieltsData.dateOfExam ? Timestamp.fromDate(ieltsData.dateOfExam) : null
            });
            await setDoc(
                doc(db, `companies/${companyId}/leads/${leadId}/academics/IELTS`),
                sanitizedIelts
            );

            // Update lead document with updatedAt and updatedBy fields
            const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            await setDoc(leadRef, {
                updatedAt: Timestamp.now(),
                updatedBy: auth.currentUser?.uid
            }, { merge: true });

            setToastMessage('Test results saved successfully!');
            setIsErrorToast(false);
            setShowToast(true);
            // Refresh the pipeline view if needed
            dispatch(setPipelineRefresh(true));
            console.log('Test results saved successfully');
        } catch (error) {
            console.error("Error saving test results:", error);
            setToastMessage('Error saving test results. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsLoading(false);
            setIsConfirmModalOpen(false);
            setAdditionalActions(true);
        }
    };

    // Add these new handler functions for additional exams
    const handleAddExamClick = () => {
        setShowAddExamPrompt(true);
    };

    const handleAddExamResponse = (response: boolean) => {
        setShowAddExamPrompt(false);
        if (response) {
            setShowAddExamForm(true); // Show the form
            setNewExam({
                id: '', // Can be empty initially
                examName: '', // This will become the Firestore ID
                dateOfExam: null,
                score: ''
            });
        }
    };

    const handleNewExamChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewExam(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNewExamDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewExam(prev => ({
            ...prev,
            dateOfExam: e.target.value ? new Date(e.target.value) : null
        }));
    };

    const handleSaveNewExam = async () => {
        if (!newExam.examName.trim() || !companyId || !leadId) return;

        try {
            // Check if the exam already exists
            const examDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}/academics/${newExam.examName}`));
            if (examDoc.exists()) {
                setToastMessage('Error: Exam already exists. Please choose a different Exam.');
                setIsErrorToast(true);
                setShowToast(true);
                return;
            }
            const acadamicsRef = doc(db, `companies/${companyId}/leads/${leadId}/academics/${newExam.examName}`);
            await setDoc(acadamicsRef, {
                score: newExam.score,
                dateOfExam: newExam.dateOfExam ? Timestamp.fromDate(newExam.dateOfExam) : null
            });

            setAdditionalExams(prev => [
                ...prev,
                {
                    ...newExam,
                    id: newExam.examName // Use examName as ID for consistency
                }
            ]);

            setNewExam({
                id: '',
                examName: '',
                dateOfExam: null,
                score: ''
            });

            setShowAddExamForm(false);
            setToastMessage('Additional exam saved successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error("Error saving additional exam:", error);
            setToastMessage('Error saving additional exam. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        }
    };

    const handleDeleteExam = async (examId: string) => {
        if (!companyId || !leadId) return;

        try {
            // Use deleteDoc instead of setDoc
            await deleteDoc(doc(db, `companies/${companyId}/leads/${leadId}/academics/${examId}`));

            setAdditionalExams(prev => prev.filter(exam => exam.id !== examId));

            setToastMessage('Exam deleted successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error("Error deleting exam:", error);
            setToastMessage('Error deleting exam. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        }
    };

    // Update the handleSave function to include additional exams


    const inputClass = `w-full py-3 px-4 bg-white border border-cyan-600 rounded-3xl focus:outline-none focus:ring-2 focus:ring-sky-950 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`;
    const labelClass = "block font-montserrat font-normal text-base text-black mb-2";
    const sectionClass = "group mb-8 bg-white rounded-lg shadow-sm transition-all duration-300 border border-gray-100";
    const sectionHeaderClass = "text-lg font-semibold text-sky-950 ";

    return (
        <div>
            <h3 className="text-xl font-semibold mb-4">Test Results</h3>

            {/* TOEFL Section */}
            <div className={`${sectionClass} ${collapsibleSections.toefl ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                <div
                    className='p-5 cursor-pointer'
                    onClick={() => setCollapsibleSections({ ...collapsibleSections, toefl: !collapsibleSections.toefl })}
                >
                    <h2 className={`${sectionHeaderClass} ${collapsibleSections.toefl ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>
                        TOEFL
                    </h2>
                </div>

                {collapsibleSections.toefl && (
                    <>
                        <div className="grid grid-cols-2 gap-6 w-[90%]">
                            <div>
                                <label className={labelClass}>Date of Exam</label>
                                <input
                                    type="date"
                                    value={toeflData.dateOfExam ? toeflData.dateOfExam.toISOString().split('T')[0] : ''}
                                    onChange={(e) => setToeflData(prev => ({
                                        ...prev,
                                        dateOfExam: e.target.value ? new Date(e.target.value) : null
                                    }))}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Speaking</label>
                                <input
                                    type="text"
                                    value={toeflData.speakingScore}
                                    onChange={(e) => setToeflData(prev => ({
                                        ...prev,
                                        speakingScore: e.target.value
                                    }))}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                    placeholder="Enter speaking score"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Reading</label>
                                <input
                                    type="text"
                                    value={toeflData.readingScore}
                                    onChange={(e) => setToeflData(prev => ({
                                        ...prev,
                                        readingScore: e.target.value
                                    }))}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                    placeholder="Enter reading score"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Writing</label>
                                <input
                                    type="text"
                                    value={toeflData.writingScore}
                                    onChange={(e) => setToeflData(prev => ({
                                        ...prev,
                                        writingScore: e.target.value
                                    }))}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                    placeholder="Enter writing score"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Listening</label>
                                <input
                                    type="text"
                                    value={toeflData.listeningScore}
                                    onChange={(e) => setToeflData(prev => ({
                                        ...prev,
                                        listeningScore: e.target.value
                                    }))}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                    placeholder="Enter listening score"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Overall Score</label>
                                <input
                                    type="text"
                                    value={toeflData.overallScore}
                                    onChange={(e) => setToeflData(prev => ({
                                        ...prev,
                                        overallScore: e.target.value
                                    }))}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                    placeholder="Enter overall score"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Test Type</label>
                                <input
                                    type="text"
                                    value={toeflData.testType}
                                    onChange={(e) => setToeflData(prev => ({
                                        ...prev,
                                        testType: e.target.value
                                    }))}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                    placeholder="Enter test type"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>TRF Number</label>
                                <input
                                    type="text"
                                    value={toeflData.TRFNumber}
                                    onChange={(e) => setToeflData(prev => ({
                                        ...prev,
                                        TRFNumber: e.target.value
                                    }))}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                    placeholder="Enter TRF number"
                                />
                            </div>
                        </div>

                        {/* File Upload Section */}
                        <div className="mt-4 w-[45%]">
                            <label className={labelClass}>Upload Test Result</label>
                            <div className="mb-6">
                                <div className="relative">
                                    <label htmlFor="file-upload" className="block">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                            <img
                                                src="/images/desktop/teenyicons.svg"
                                                alt="icon"
                                                className="w-8 h-8 text-gray-400"
                                            />
                                        </span>
                                        <div className="absolute left-16 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                                            {uploadedFileName ? (
                                                <div className="flex items-center">
                                                    <span
                                                        className="text-blue-600 cursor-pointer hover:text-blue-800"
                                                        onClick={handleFileDownload}
                                                    >
                                                        {uploadedFileName}
                                                    </span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleFileDelete();
                                                        }}
                                                        className="px-3 mr-4 py-1 text-sm text-white rounded-full bg-gradient-to-r from-[#FFA782] to-[#FC583D] hover:opacity-90"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    No file selected<br />
                                                    <span className="text-gray-500">Click to upload</span>
                                                </>
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            className={`hidden ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                            disabled={!write}
                                            accept=".pdf,.jpg,.jpeg"
                                            onChange={handleFileUpload}
                                        />
                                        <div
                                            className={`w-full py-6 h-16 pl-14 pr-4 bg-white border border-cyan-600 rounded-3xl cursor-pointer hover:bg-gray-50 focus-within:ring-2 focus-within:ring-sky-950 ${isUploading ? 'opacity-75 cursor-not-allowed' : ''
                                                }`}
                                            onClick={() => !isUploading && fileInputRef.current?.click()}
                                        >
                                            {isUploading && (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                                                </div>
                                            )}
                                            <span className="sr-only">Choose file</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* IELTS Section */}
            <div className={`${sectionClass} ${collapsibleSections.ielts ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                <div
                    className='p-5 cursor-pointer'
                    onClick={() => setCollapsibleSections({ ...collapsibleSections, ielts: !collapsibleSections.ielts })}
                >
                    <h2 className={`${sectionHeaderClass} ${collapsibleSections.ielts ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>
                        IELTS
                    </h2>
                </div>

                {collapsibleSections.ielts && (
                    <div className="grid grid-cols-2 gap-6 w-[90%]">
                        <div>
                            <label className={labelClass}>Date of Exam</label>
                            <input
                                type="date"
                                value={ieltsData.dateOfExam ? ieltsData.dateOfExam.toISOString().split('T')[0] : ''}
                                onChange={(e) => setIeltsData(prev => ({
                                    ...prev,
                                    dateOfExam: e.target.value ? new Date(e.target.value) : null
                                }))}
                                className={inputClass}
                                readOnly={!write}
                                disabled={!write}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Speaking</label>
                            <input
                                type="text"
                                value={ieltsData.speakingScore}
                                onChange={(e) => setIeltsData(prev => ({
                                    ...prev,
                                    speakingScore: e.target.value
                                }))}
                                className={inputClass}
                                readOnly={!write}
                                disabled={!write}
                                placeholder="Enter speaking score"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Reading</label>
                            <input
                                type="text"
                                value={ieltsData.readingScore}
                                onChange={(e) => setIeltsData(prev => ({
                                    ...prev,
                                    readingScore: e.target.value
                                }))}
                                className={inputClass}
                                readOnly={!write}
                                disabled={!write}
                                placeholder="Enter reading score"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Writing</label>
                            <input
                                type="text"
                                value={ieltsData.writingScore}
                                onChange={(e) => setIeltsData(prev => ({
                                    ...prev,
                                    writingScore: e.target.value
                                }))}
                                className={inputClass}
                                readOnly={!write}
                                disabled={!write}
                                placeholder="Enter writing score"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Listening</label>
                            <input
                                type="text"
                                value={ieltsData.listeningScore}
                                onChange={(e) => setIeltsData(prev => ({
                                    ...prev,
                                    listeningScore: e.target.value
                                }))}
                                className={inputClass}
                                readOnly={!write}
                                disabled={!write}
                                placeholder="Enter listening score"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Additional Exams Section */}
            {/* <div className="mb-8"> */}
            <div className="flex justify-start items-center mb-4 gap-2">
                <h3 className="text-xl font-semibold mb-4">Other Exams</h3>
                {write && (
                    <button
                        onClick={handleAddExamClick}
                        className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white rounded-full hover:bg-blue-600"
                    >
                        +
                    </button>
                )}
            </div>

            {/* Add Exam Prompt */}
            {showAddExamPrompt && (
                <div className="mb-4 p-4 bg-gray-100 rounded-lg">
                    <p className="mb-2">Would you like to add another exam?</p>
                    <div className="flex space-x-4">
                        <button
                            onClick={() => handleAddExamResponse(true)}
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                            Yes
                        </button>
                        <button
                            onClick={() => handleAddExamResponse(false)}
                            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                            No
                        </button>
                    </div>
                </div>
            )}

            {/* New Exam Form */}
            {showAddExamForm && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h5 className="text-md font-medium mb-3">Add New Exam</h5>
                    <div className="grid grid-cols-3 gap-4">
                        {/* Form fields remain the same */}
                        <div>
                            <label className={labelClass}>Exam Name</label>
                            <input
                                type="text"
                                name="examName"
                                value={newExam.examName}
                                onChange={handleNewExamChange}
                                className={inputClass}
                                placeholder="Enter exam name"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Date of Exam</label>
                            <input
                                type="date"
                                value={newExam.dateOfExam ? newExam.dateOfExam.toISOString().split('T')[0] : ''}
                                onChange={handleNewExamDateChange}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Score</label>
                            <input
                                type="text"
                                name="score"
                                value={newExam.score}
                                onChange={handleNewExamChange}
                                className={inputClass}
                                placeholder="Enter score"
                            />
                        </div>
                    </div>
                    <div className="mt-3 flex justify-end space-x-2">
                        <button
                            onClick={() => setShowAddExamForm(false)} // Close form
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveNewExam}
                            disabled={!newExam.examName.trim()}
                            className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ${!newExam.examName.trim() ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                        >
                            Save Exam
                        </button>
                    </div>
                </div>
            )}
            {/* List of Additional Exams */}
            {additionalExams.length > 0 && (
                <>
                    {additionalExams.map((exam) => (
                        <div key={exam.id} className={`${sectionClass} ${collapsibleAdditionalExams[exam.id] ? 'p-6 border-b-2 ring-1 ring-cyan-500' : 'p-0'}`}>
                            <div className="p-5 cursor-pointer flex justify-between items-start"
                                onClick={() => setCollapsibleAdditionalExams({ ...collapsibleAdditionalExams, [exam.id]: !collapsibleAdditionalExams[exam.id] })}
                            >
                                <h2 className={`${sectionHeaderClass} ${collapsibleAdditionalExams[exam.id] ? 'mb-4 pb-2 border-b border-gray-200' : 'mb-2 pb-0 border-0'}`}>
                                    {exam.examName}
                                </h2>
                                {write && (
                                    <button
                                        onClick={() => handleDeleteExam(String(exam.id))}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>

                            {collapsibleAdditionalExams[exam.id] && (
                                <div className="grid grid-cols-2 gap-4 mt-2">
                                    <div>
                                        <label className={labelClass}>Date of Exam</label>
                                        <input
                                            type="date"
                                            value={exam.dateOfExam ? exam.dateOfExam.toISOString().split('T')[0] : ''}
                                            className={inputClass}
                                            readOnly
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Score</label>
                                        <input
                                            type="text"
                                            value={exam.score}
                                            className={inputClass}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </>
            )}

            {additionalExams.length === 0 && !newExam.id && (
                <p className="text-gray-500">No additional exams added yet.</p>
            )}
            {/* </div> */}

            {/* Save Button */}
            {write && (
                <div className="flex items-center justify-center h-full">
                    <button
                        onClick={confirmSave}
                        disabled={isLoading}
                        className={`text-center p-3 text-white bg-gradient-to-r from-[#3483BE] to-[#02385E] mt-5 mb-5 rounded-3xl font-medium hover:bg-gray-50 relative ${isLoading ? 'cursor-not-allowed opacity-75' : ''
                            }`}
                    >
                        <span className={isLoading ? 'invisible' : ''}>Save & Proceed</span>
                        {isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                            </div>
                        )}
                    </button>
                </div>
            )}

            <ConfirmationModal isOpen={isConfirmModalOpen} isAdditional={isAdditionalActions} onClose={() => setIsConfirmModalOpen(false)} onSave={handleSave}
                selectedStudent={{ id: leadId, name: leadName, phone: leadPhone }} />

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};
export default TestResults;


